/* eslint-disable @typescript-eslint/no-unused-vars */
import { ArrowIcon, Box } from '@modusign/design-system';
import cx from 'classnames';
import { MouseEvent, useRef } from 'react';
import DropZone, { DropzoneRef } from 'react-dropzone';

import checkIconImage from '@app/assets/icons/icon-check.svg';
import boxImage from '@app/assets/modal/upload-modal/box.svg';
import dropBoxImage from '@app/assets/modal/upload-modal/dropbox.svg';
import googleDriveImage from '@app/assets/modal/upload-modal/googledrive.svg';
import oneDriveImage from '@app/assets/modal/upload-modal/onedrive.svg';
import localImage from '@app/assets/modal/upload-modal/pc.jpg';
import UploadedFileInformation from '@app/components/UploadedFileInformation';
import GOOGLE_CLIENT_ID from '@app/constants/googleClientId';
import { ACCEPT_FILETYPE } from '@app/constants/UpLoadModal';
import importScript from '@app/libs/importScript';
import UploadFile from '@app/store/documents/UploadFile';

import UploadType from '../../UploadType';

import './UpLoadModalDropZone.scss';

importScript('//www.dropbox.com/static/api/2/dropins.js', { id: 'dropboxjs' });
importScript('//apis.google.com/js/api.js');
importScript('//accounts.google.com/gsi/client');
importScript('https://cdn01.boxcdn.net/js/static/select.js');
importScript('//js.live.net/v7.2/OneDrive.js');

export const getUploadHandlerImg = (type: UploadType) => {
  switch (type) {
    case 'BOX':
      return boxImage;
    case 'DROPBOX':
      return dropBoxImage;
    case 'GOOGLE_DRIVE':
      return googleDriveImage;
    case 'ONE_DRIVE':
      return oneDriveImage;
    case 'LOCAL':
      return localImage;
    default:
      return null;
  }
};

export const getUploadHandlerName = (type: UploadType) => {
  switch (type) {
    case 'BOX':
      return 'Box';
    case 'DROPBOX':
      return 'DropBox';
    case 'GOOGLE_DRIVE':
      return 'Google Drive';
    case 'ONE_DRIVE':
      return 'One Drive';
    case 'LOCAL':
      return '내 컴퓨터';
    default:
      return null;
  }
};

interface UpLoadModalDropZoneProps {
  fileName: string | null;
  uploadType: UploadType;
  onDrop: (acceptFile: Array<File | UploadFile>) => void;
  onReset: () => void;
}

const defaultProps = {
  fileName: null,
};

function UpLoadModalDropZone({ fileName, uploadType, onDrop, onReset }: UpLoadModalDropZoneProps) {
  const dropzoneRef = useRef<DropzoneRef>(null);

  function handleFileSelectClick(event: MouseEvent<HTMLButtonElement>) {
    if (uploadType === 'LOCAL') {
      event.preventDefault();
      dropzoneRef?.current?.open();
    }

    if (uploadType === 'DROPBOX') {
      /**
       * @name DropboxChooser()
       * @brief Dropbox를 사용한 업로드 함수
       * @DoKnow Dropbox에서 localHost를 지원함.
       */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.Dropbox.appKey = 'gsym7hzcp0hladd';
      const options = {
        success: (files: Array<{ link: string; name: string; bytes: number }>) => {
          const dropboxFile: Array<UploadFile> = [
            {
              downloadLink: files[0].link,
              name: files[0].name,
              size: files[0].bytes,
            },
          ];
          onDrop(dropboxFile);
        },
        linkType: 'direct',
        multiselect: false,
        extensions: ACCEPT_FILETYPE,
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.Dropbox.choose(options);
    }

    if (uploadType === 'GOOGLE_DRIVE') {
      const clientId = GOOGLE_CLIENT_ID;
      const scope = 'https://www.googleapis.com/auth/drive.readonly';
      let oauthToken: string;

      const onChange = (data: {
        action: string;
        docs: Array<{
          id: string;
          mimeType: string;
          name: string;
          sizeBytes: number;
          type: string;
        }>;
      }) => {
        if (data.action === 'picked') {
          const googleDriveFile = [
            {
              accessToken: oauthToken,
              downloadLink: `https://www.googleapis.com/drive/v3/files/${data.docs[0].id}?alt=media`,
              mimeType: data.docs[0].mimeType,
              name: data.docs[0].name,
              size: data.docs[0].sizeBytes,
            },
          ];

          /**
           * google document의 문서를 바로 업로드하기 위해 변환 과정
           * google document의 종류는 doc, pres, spread 3종류
           * type이 'document'일 경우 google document의 종류이며,
           * 그 중 doc, pres, spread 파일 모두 `.pdf`로 변환해서 업로드합니다.
           * google upload 의 로컬 테스트는 `127.0.0.1:3000`에서 불가능 (localhost:3000에서 가능)
           */
          if (data.docs[0].type === 'document') {
            googleDriveFile[0].downloadLink = `https://www.googleapis.com/drive/v3/files/${data.docs[0].id}/export?mimeType=application/pdf`;
            googleDriveFile[0].name = `${googleDriveFile[0].name}.pdf`;
          }

          onDrop(googleDriveFile);
        }
      };

      const createPicker = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const picker = new window.google.picker.PickerBuilder()
          .setOrigin(`${window.location.protocol}//${window.location.host}`)
          .setLocale('ko')
          .setOAuthToken(oauthToken)
          .setCallback(onChange)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .addView(new window.google.picker.DocsView());

        picker.build().setVisible(true);
      };

      // eslint-disable-next-line camelcase
      const handleAuthResult = (authResult: { access_token: string; error?: Error }) => {
        if (authResult.error) {
          // eslint-disable-next-line no-console
          console.error('Error occurred in google OAuth result: ', authResult);
        } else if (authResult) {
          oauthToken = authResult.access_token;
          createPicker();
        }
      };

      const onLoad = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          // eslint-disable-next-line camelcase
          client_id: clientId,
          scope,
          callback: handleAuthResult,
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.gapi.load('picker');

        if (oauthToken) {
          createPicker();
        }
        tokenClient.requestAccessToken();
      };
      onLoad();
    }

    if (uploadType === 'BOX') {
      const options = {
        clientId: 'kqind17dbq05cvhq066e5pqn5bhxl2pd',
        linkType: 'direct',
        multiselect: false,
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const boxSelect = new window.BoxSelect(options);
      boxSelect.success((files: Array<{ name: string; size: number; url: string }>) => {
        const BoxFile = [
          {
            size: files[0].size,
            downloadLink: files[0].url,
            name: files[0].name,
          },
        ];
        onDrop(BoxFile);
      });

      boxSelect.launchPopup();
    }

    if (uploadType === 'ONE_DRIVE') {
      const options = {
        clientId: '14e1f50c-cd28-4866-b43e-f03702c29e7f',
        action: 'download',
        multiSelect: false,
        openInNewWindow: true,
        success: (files: {
          value: Array<{ name: string; size: number; '@microsoft.graph.downloadUrl': string }>;
        }) => {
          const OneDriveFile = [
            {
              size: files.value[0].size,
              downloadLink: files.value[0]['@microsoft.graph.downloadUrl'],
              name: files.value[0].name,
            },
          ];
          onDrop(OneDriveFile);
        },
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.OneDrive.open(options);
    }
  }

  return (
    <Box flexGrow={1} pb={4} pt={7} px={7} textAlign="center">
      {!fileName && (
        <DropZone
          maxFiles={1}
          multiple={false}
          noClick
          ref={dropzoneRef}
          useFsAccessApi={false}
          onDrop={onDrop}
        >
          {({ getRootProps, getInputProps }) => (
            <div className="UploadModalDropZone__dropzone" {...getRootProps()}>
              <input {...getInputProps()} />
              <div
                className={cx({
                  'UploadModalDropZone__image-box': uploadType === 'LOCAL',
                })}
              >
                <img alt="" src={getUploadHandlerImg(uploadType)} />
              </div>
              <p>
                이곳에 파일을 끌어다 놓거나, <strong>{getUploadHandlerName(uploadType)}</strong>
                에서
                <br /> 업로드할 문서를 선택해 주세요.
              </p>
              <button
                className={cx('Btn space-3x', {
                  'UploadModalDropZone__upload-btn UploadModalDropZone__upload-btn--local':
                    uploadType === 'LOCAL',
                  'UploadModalDropZone__upload-btn UploadModalDropZone__upload-btn--google-drive':
                    uploadType === 'GOOGLE_DRIVE',
                  'UploadModalDropZone__upload-btn UploadModalDropZone__upload-btn--dropbox':
                    uploadType === 'DROPBOX',
                  'UploadModalDropZone__upload-btn UploadModalDropZone__upload-btn--one-drive':
                    uploadType === 'ONE_DRIVE',
                  'UploadModalDropZone__upload-btn UploadModalDropZone__upload-btn--box':
                    uploadType === 'BOX',
                })}
                type="button"
                onClick={handleFileSelectClick}
              >
                파일 찾기
                <ArrowIcon direction="right" ml="2px" />
              </button>
              <div className="UploadModalDropZone__sub-texts Card Card--primary Card--theme-flat align-left box-2x space-1x">
                <ul className="CheckList">
                  <li className="space-1x">
                    <small>지원 파일 : HWP, DOCX, XLSX, PPTX, PDF, JPG, JPEG, PNG 등</small>
                  </li>
                  <li>
                    <small>업로드 가능 용량 : 최대 5MB</small>
                  </li>
                </ul>
              </div>
              <div className="align-right space-2x">
                <small>
                  <a
                    className="TextBtn TextBtn--underline"
                    href="https://support.modusign.co.kr/hc/ko/articles/360000525428"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    문서 업로드 도움말
                    <ArrowIcon direction="right" ml="2px" />
                  </a>
                </small>
              </div>
            </div>
          )}
        </DropZone>
      )}
      {fileName && (
        <div>
          <img alt="" className="UploadModalDropZone__upload-done-image" src={checkIconImage} />
          <p>
            문서 선택이 완료되었습니다.
            <br />
            이제 문서를 업로드할 수 있습니다.
          </p>
          <UploadedFileInformation fileName={fileName} onReset={onReset} />
        </div>
      )}
    </Box>
  );
}

UpLoadModalDropZone.defaultProps = defaultProps;

export default UpLoadModalDropZone;
